import React from "react";
import { Redirect } from "react-router";
import { StartupPageTemplate } from "../components/startup-pages/StartupPageTemplate";
import { loginRequest } from "../configs/msalConfig";
import * as authService from "../services/authService";
import { login, getConsent } from "../services/dashApiService";
import { queueToast } from "../services/systemService";
import { dashboard } from "../configs/routes";
import {
	loadUserPid,
	loadUserSettings,
	setDiseaseGroup,
	setIsDemoUser,
	setIsRegistrar,
	setAppSettings,
	setIsLoggedIn,
} from "../services/userService";
import { LoadingSpinnerAlt } from "../components/common/LoadingSpinnerAlt";
import { decodeJwt } from "jose";
import { DiseaseGroupContext } from "../contexts/diseaseGroupContext";
import { isMobile, getUA, deviceType, deviceDetect } from "react-device-detect";
import { releaseDateLabel } from "../configs/config.json";
import { getCurrentAppVersion } from "../services/mobileService.js";
import { toast } from "react-toastify";

export default class Login extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			consentConfirmed: false,
			userDetails: {},
			isComplete: false,
			isDemoUser: false,
			isRegistrar: false,
			lastLogin: null,
		};
	}

	componentDidMount() {
		const b2cToken = authService.loadB2CToken();
		if (b2cToken) {
			const b2cClaims = decodeJwt(b2cToken);
			const expireSeconds = b2cClaims.exp;
			const isExpired = expireSeconds * 1000 < new Date().getTime();

			if (isExpired) {
				console.error("Login token expired on open, logging out automatically");
				authService.logout(this.props.msalInstance, this.props.msalAccount);
				return;
			}
		}

		const acquireTokenRequest = {
			...loginRequest,
			account: this.props.msalAccount,
		};

		const email = authService.loadB2CEmail();

		if (email) {
			acquireTokenRequest.loginHint = email;
		}

		const onError = (error) => {
			console.error(error);
			if (error.errorCode === "unsupported_app") {
				queueToast({ message: error.message, options: { type: "error" } });
			} else {
				queueToast("Error signing in, please sign in again");
			}

			authService.logout(this.props.msalInstance, this.props.msalAccount);
		};

		const requestLogin = (version) => {
			this.props.msalInstance
				.acquireTokenSilent(acquireTokenRequest)
				.then((acquireTokenResponse) => {
					const b2cEmail = acquireTokenResponse.idTokenClaims.emails[0];
					authService.saveB2CEmail(b2cEmail);

					const b2cToken = acquireTokenResponse.idToken;
					authService.saveB2CToken(b2cToken);

					const data = {
						isMobile,
						getUA,
						deviceType,
						deviceDetect: deviceDetect(getUA),
						releaseDateLabel,
						version,
					};

					login(b2cToken, data)
						.then((loginData) => {
							setIsLoggedIn(true);
							setIsDemoUser(loginData.data.isDemoUser);
							setAppSettings(loginData.data.appSettings);
							setIsRegistrar(
								decodeJwt(authService.loadDashToken())?.registrar ?? false
							);
							authService.saveAutoLoginToken(loginData.data.autoLoginToken);

							const diseaseGroup = loginData.data.diseaseGroup;

							if (diseaseGroup) {
								setDiseaseGroup(diseaseGroup);
							}

							this.setState({ lastLogin: loginData.data.lastLogin });

							// do we want to display any message
							if (loginData.data.message) {
								toast(
									loginData.data.message.message,
									loginData.data.message.options || {}
								);
							}

							const promises = [
								getConsent(),
								loadUserPid(),
								loadUserSettings(),
							];

							promises[0].then((consentData) => {
								if (consentData.data.consentConfirmed) {
									this.setState({
										consentConfirmed: true,
									});
								}
							});

							promises[1].then((data) => {
								if (data.success === true) {
									this.setState({
										userDetails: data.data,
									});
								}
							});

							// loadUserSettings();

							Promise.all(promises)
								.then(() => {
									this.setState({ isComplete: true });
									this.context.setTheme(diseaseGroup);
								})
								.catch(onError);
						})
						.catch(onError);
				})
				.catch((error) => {
					console.error(error);
					if (
						error.errorCode === "interaction_required" ||
						error.errorCode === "login_required"
					) {
					} else {
						queueToast("Error signing in, please sign in again");
					}

					authService.logout(this.props.msalInstance, this.props.msalAccount);
				});
		};

		getCurrentAppVersion()
			.then((version) => {
				requestLogin(version);
			})
			.catch(() => {
				requestLogin("error");
			});
	}

	render() {
		if (this.state.isComplete) {
			return this.state.consentConfirmed && this.state.lastLogin ? (
				<Redirect
					to={{
						pathname: dashboard,
						state: { isDemoUser: this.state.isDemoUser },
					}}
				/>
			) : this.state.consentConfirmed ? (
				<Redirect
					to={{
						pathname: "/loadingdata",
						state: { isDemoUser: this.state.isDemoUser },
					}}
				/>
			) : (
				<Redirect
					to={{
						pathname: "/setup",
						state: { userDetails: this.state.userDetails },
					}}
				/>
			);
		} else {
			return (
				<StartupPageTemplate>
					<LoadingSpinnerAlt />
				</StartupPageTemplate>
			);
		}
	}
}

Login.contextType = DiseaseGroupContext;
